import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { track } from './track';
import { EVENTS } from './events';

/**
 * Emits page_view on every route change.
 *
 * The path is normalized to strip ids (UUIDs and numerics) so we get
 * stable aggregations like `/customers/:id/show` instead of one bucket
 * per customer.
 */
const normalizePath = (pathname: string): string =>
  pathname
    .split('/')
    .map(segment => {
      if (/^\d+$/.test(segment)) return ':id';
      if (/^[0-9a-f]{8}-[0-9a-f]{4}-/i.test(segment)) return ':uuid';
      return segment;
    })
    .join('/') || '/';

export const usePageTracking = (): void => {
  const location = useLocation();
  const lastPath = useRef<string | null>(null);

  useEffect(() => {
    const fullPath = `${location.pathname}${location.search}`;
    if (lastPath.current === fullPath) return;
    lastPath.current = fullPath;

    track(EVENTS.PAGE_VIEW, {
      path: normalizePath(location.pathname),
      raw_path: location.pathname,
      has_query: location.search.length > 0,
    });
  }, [location.pathname, location.search]);
};
